/*
 * Lock down an app with authentiction
 */

import React, { useState, useRef } from 'react';
import IdleTimer from 'react-idle-timer';
import { createClient } from 'src/client';
import { App } from 'src/App';
// state provider
import { GlobalStateProvider } from 'src/@app/providers/GlobalStateProvider';
// components
import { PageSkeleton } from 'src/components';
import { StartScreen } from 'src/@auth/components/StartScreen';
// utils
import {
  getIdleTimeout,
  initiateAuthCodeflow,
  defineAuthState,
  getToken,
  processAuthResponse,
  signOut,
} from 'src/@auth/functions/core';
import { removeAuth } from 'src/@auth/functions/storage';

// define app's authentication state as soon as index loads
const { state, query } = defineAuthState();

export const Authenticated = ({ client }) => {
  const timerRef = useRef(null);

  const appOnIdle = () => {
    console.log(`Logged out automatically due to no activity.`);
    signOut('001');
  };

  return (
    <GlobalStateProvider>
      <IdleTimer ref={timerRef} onIdle={appOnIdle} debounce={250} timeout={getIdleTimeout()} />
      <App client={client} />
    </GlobalStateProvider>
  );
};

export const Authentication = () => {
  const [isAuthFailed, authFailed] = useState<boolean>(false);
  const [authData, updateAuthData] = useState<null | {
    authorization: string;
    refreshToken: string;
    refreshTokenExpiry: number;
  }>(null);

  if (isAuthFailed) {
    removeAuth();
    return <StartScreen clickEvent={initiateAuthCodeflow} error={'failed'} />;
  } else if (authData) {
    return <Authenticated client={createClient(authData)} />;
  } else {
    if (state === 'start') {
      const { text, error, displayButton } = query;
      return (
        <StartScreen
          clickEvent={initiateAuthCodeflow}
          text={text}
          error={error}
          displayButton={displayButton}
        />
      );
    } else if (state === 'code') {
      getToken(query)
        .then(response => {
          updateAuthData(processAuthResponse(response));
        })
        .catch(() => {
          authFailed(true);
        });
    } else if (state === 'valid') {
      // page is refreshed and the access token is still valid
      const client = createClient({
        authorization: query.authorization,
        refreshToken: null,
        refreshTokenExpiry: 0,
      });
      return <Authenticated client={client} />;
    } else if (state === 'signout') {
      if (query.removeAuth) {
        removeAuth();
      }
      return <StartScreen clickEvent={initiateAuthCodeflow} text={query.text} />;
    }
  }

  return <PageSkeleton />;
};
